import type { QueryResult } from "@kv/apollo-client";
import type { FC } from "react";
import {
  createContext,
  useContext,
  useMemo,
  useEffect,
  useCallback,
} from "react";
import { useHistory } from "react-router-dom";
import useCookie from "react-use-cookie";

import { useGetAuthTokensQuery } from "~/hooks/graphql/auth";
// eslint-disable-next-line import/order
import type { AuthTokensQueryResult } from "~/types";

//* We use this context in authenticated query/mutation hooks so we import
//* this query directly from its file to avoid circular dependencies
import { getIsValidToken } from "~/utils";



export interface AuthTokensContextProps {
  loading: QueryResult["loading"];
  error?: QueryResult["error"];
  authTokens?: AuthTokensQueryResult["authTokens"];
  isAuthenticated: boolean;
  refetch: () => void;
}

const defaultContextValue: AuthTokensContextProps = {
  loading: false,
  error: undefined,
  authTokens: undefined,
  isAuthenticated: false,
  refetch: () => undefined,
};

const AuthTokensContext = createContext(defaultContextValue);

export const AuthTokensProvider: FC = ({ children }) => {
  const history = useHistory();

  const { data, loading, error, refetch } = useGetAuthTokensQuery();
  const [userToken, setAccessToken] = useCookie("token", "none");

  const getAccessTokenFromQuery = useCallback(() => {
    if (data?.authTokens) {
      const _token = data?.authTokens.accessToken;

      setAccessToken(_token, {
        days: 0.33, // 8hrs
      });

      return _token;
    }
  }, [data?.authTokens, setAccessToken]);

  const accessToken = useMemo(() => {
    // get it from the cookie
    if (userToken !== "none") {
      return userToken;
    } else if (!loading) {
      // try and get it from the data
      return getAccessTokenFromQuery();
    }
  }, [getAccessTokenFromQuery, loading, userToken]);

  const refetchAuthToken = useCallback(() => {
    const _token = getAccessTokenFromQuery();
    if (!_token) {
      refetch();
    }
  }, [getAccessTokenFromQuery, refetch]);

  useEffect(
    () => history.listen(() => refetchAuthToken()),
    [history, refetchAuthToken],
  );

  const contextValue = useMemo(
    () => ({
      loading,
      error,
      refetch,
      isAuthenticated: getIsValidToken(accessToken),
    }),
    [error, loading, refetch, accessToken],
  );

  return (
    <AuthTokensContext.Provider value={contextValue}>
      {children}
    </AuthTokensContext.Provider>
  );
};

export const useAuthTokensContext = () => useContext(AuthTokensContext);
